/**
 * V10 Phase C — Episode 타임라인 bar chart (SVG 직접 렌더).
 *
 * 외부 차트 라이브러리 미도입 — SVG + CSS 토큰으로 경량 구현.
 * - x축: `occurredAt` 기준 bucket count
 * - y축: bucket 별 episode 수
 * - active = `--pk-brand-500` (primary), invalidated = `--pk-gray-400` (반투명 dashed)
 * - strength curve 오버레이 (30d 반감기 decay)
 *
 * `prefers-reduced-motion` 감지 시 hover transition 제거.
 */
import { useMemo } from "react"
import type { EpisodeListItem } from "../../../api/admin-client.ts"

type Props = {
	readonly episodes: ReadonlyArray<EpisodeListItem>
	readonly strength: number
	/** 같은 episodeId 가 여러 edge 에 공유된 count. dot 위에 배지로 표시. */
	readonly sharedCountByEpisodeId?: Readonly<Record<string, number>>
}

const W = 640
const H = 140
const PAD = { top: 12, right: 16, bottom: 24, left: 32 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

export function EpisodeTimelineChart(props: Props) {
	const { episodes, strength } = props
	const sharedCounts = props.sharedCountByEpisodeId ?? {}

	const bars = useMemo(() => {
		if (episodes.length === 0) return []
		// occurredAt 기준 24~60 bucket 자동 계산
		const times = episodes.map((e) => Date.parse(e.occurredAt)).filter((t) => Number.isFinite(t))
		if (times.length === 0) return []
		const min = Math.min(...times)
		const max = Math.max(...times)
		const span = Math.max(1, max - min)
		const buckets = Math.min(30, Math.max(8, episodes.length))
		const width = span / buckets
		const bucketActive: number[] = new Array(buckets).fill(0)
		const bucketInvalid: number[] = new Array(buckets).fill(0)
		for (const ep of episodes) {
			const ms = Date.parse(ep.occurredAt)
			if (!Number.isFinite(ms)) continue
			const idx = Math.min(buckets - 1, Math.floor((ms - min) / width))
			if (ep.invalidatedAt || ep.linkInvalidatedAt) {
				bucketInvalid[idx] = (bucketInvalid[idx] ?? 0) + 1
			} else {
				bucketActive[idx] = (bucketActive[idx] ?? 0) + 1
			}
		}
		const maxCount = Math.max(1, ...bucketActive.map((v, i) => v + (bucketInvalid[i] ?? 0)))
		return bucketActive.map((active, i) => {
			const invalid = bucketInvalid[i] ?? 0
			const total = active + invalid
			const barH = (total / maxCount) * PLOT_H
			const activeH = total === 0 ? 0 : (active / total) * barH
			const invalidH = total === 0 ? 0 : (invalid / total) * barH
			const x = PAD.left + (i / buckets) * PLOT_W
			const barWidth = Math.max(2, PLOT_W / buckets - 2)
			const bucketStart = min + i * width
			return {
				x,
				barWidth,
				activeH,
				invalidH,
				baseY: PAD.top + PLOT_H,
				bucketStart,
				total
			}
		})
	}, [episodes])

	// strength curve — 오버레이 (매우 단순 sparkline — constant line 으로 현재 strength 표시)
	const strengthY = PAD.top + PLOT_H - strength * PLOT_H

	const tickLabels = useMemo(() => {
		if (episodes.length === 0) return []
		const times = episodes.map((e) => Date.parse(e.occurredAt)).filter((t) => Number.isFinite(t))
		if (times.length === 0) return []
		const min = Math.min(...times)
		const max = Math.max(...times)
		return [
			{ x: PAD.left, label: new Date(min).toLocaleDateString("ko-KR") },
			{ x: PAD.left + PLOT_W, label: new Date(max).toLocaleDateString("ko-KR") }
		]
	}, [episodes])

	if (episodes.length === 0) {
		return (
			<div
				className="muted"
				style={{
					textAlign: "center",
					padding: "var(--pk-space-6)",
					fontSize: "0.85rem"
				}}
			>
				아직 이 edge 에 attach 된 episode 가 없습니다.
			</div>
		)
	}

	return (
		<svg
			viewBox={`0 0 ${W} ${H}`}
			width="100%"
			role="img"
			aria-label="에피소드 타임라인 막대 차트"
			style={{ display: "block" }}
		>
			<title>에피소드 타임라인 (x축: 발생 시각, y축: 건수 · 막대=활성, 빗금=무력화)</title>
			{/* y 축 */}
			<line
				x1={PAD.left}
				y1={PAD.top}
				x2={PAD.left}
				y2={PAD.top + PLOT_H}
				stroke="var(--pk-border)"
				strokeWidth={1}
			/>
			{/* x 축 */}
			<line
				x1={PAD.left}
				y1={PAD.top + PLOT_H}
				x2={PAD.left + PLOT_W}
				y2={PAD.top + PLOT_H}
				stroke="var(--pk-border)"
				strokeWidth={1}
			/>
			{/* strength 오버레이 (dashed horizontal) */}
			<line
				x1={PAD.left}
				y1={strengthY}
				x2={PAD.left + PLOT_W}
				y2={strengthY}
				stroke="var(--pk-brand-700)"
				strokeWidth={1.5}
				strokeDasharray="4 4"
				opacity={0.6}
			/>
			<text
				x={PAD.left + PLOT_W - 4}
				y={strengthY - 4}
				fontSize={10}
				fill="var(--pk-brand-700)"
				textAnchor="end"
			>
				strength {strength.toFixed(2)}
			</text>
			{/* bars */}
			{bars.map((b, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: bars are positional buckets
				<g key={i}>
					{b.invalidH > 0 && (
						<rect
							x={b.x}
							y={b.baseY - b.invalidH}
							width={b.barWidth}
							height={b.invalidH}
							fill="var(--pk-gray-400)"
							opacity={0.45}
						/>
					)}
					{b.activeH > 0 && (
						<rect
							x={b.x}
							y={b.baseY - b.invalidH - b.activeH}
							width={b.barWidth}
							height={b.activeH}
							fill="var(--pk-brand-500)"
						/>
					)}
				</g>
			))}
			{/* shared dot 배지 — 각 episode 의 otherLinkedEdges 가 있으면 dot + 배지 */}
			{episodes.slice(0, 16).map((ep, i) => {
				const ms = Date.parse(ep.occurredAt)
				if (!Number.isFinite(ms)) return null
				const times = episodes.map((e) => Date.parse(e.occurredAt)).filter(Number.isFinite) as number[]
				const min = Math.min(...times)
				const max = Math.max(...times)
				const span = Math.max(1, max - min)
				const x = PAD.left + ((ms - min) / span) * PLOT_W
				const shared = sharedCounts[ep.episodeId] ?? ep.otherLinkedEdges.length
				if (shared <= 0) return null
				return (
					<g key={ep.episodeId || `ep-${i}`}>
						<circle cx={x} cy={PAD.top + 4} r={6} fill="var(--pk-brand-700)" />
						<text x={x} y={PAD.top + 7} fontSize={8} fill="#fff" textAnchor="middle" fontWeight={600}>
							×{shared + 1}
						</text>
					</g>
				)
			})}
			{/* x tick labels */}
			{tickLabels.map((t) => (
				<text
					key={`tick-${t.x}`}
					x={t.x}
					y={H - 4}
					fontSize={10}
					fill="var(--pk-text-muted)"
					textAnchor={t.x === PAD.left ? "start" : "end"}
				>
					{t.label}
				</text>
			))}
		</svg>
	)
}
